import React, { ReactNode } from 'react';
import {
  TEAM_SETUP_AREAS_UPDATED,
  TeamSetupArea,
  useSetupAreaIdContextRequired,
  useTeamSetupAreasService
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { LoadingAnimation, PopupContext } from 'ui-builder';

type TeamSetupAreaContextType = {
  teamSetupArea: TeamSetupArea
}

const TeamSetupAreaReactContext = React.createContext<TeamSetupAreaContextType | null>(null);

export function useTeamSetupAreaContextRequired(): TeamSetupAreaContextType {
  const context = React.useContext(TeamSetupAreaReactContext);
  if (!context) {
    throw new Error('useTeamSetupAreaContext must be used within a TeamSetupAreaProvider');
  }
  return context;
}

export function useTeamSetupAreaContext(): TeamSetupAreaContextType|null {
  return React.useContext(TeamSetupAreaReactContext);
}

export function TeamSetupAreaContext(
  props: {
    children: ReactNode,
  }
) {
  const teamSetupAreaService = useTeamSetupAreasService();

  const { setupAreaId } = useSetupAreaIdContextRequired();

  const { data } = useDataLoaderNew(
    () => teamSetupAreaService.getTeamSetupArea(setupAreaId),
    {
      events: [TEAM_SETUP_AREAS_UPDATED]
    }
  );

  if (data) {
    return (
      <TeamSetupAreaReactContext.Provider
        value={{
          teamSetupArea: data
        }}
      >
        <PopupContext>
          {props.children}
        </PopupContext>
      </TeamSetupAreaReactContext.Provider>
    );
  } else {
    return (<LoadingAnimation />);
  }
}

type AllTeamSetupAreasContextType = {
  areas: TeamSetupArea[]
}

const AllTeamSetupAreasReactContext = React.createContext<AllTeamSetupAreasContextType | null>(null);

export function AllTeamSetupAreasContext(
  props: {
    children: ReactNode,
  }
) {
  const teamSetupAreaService = useTeamSetupAreasService();

  const { data } = useDataLoaderNew(
    teamSetupAreaService.getTeamSetupAreas,
    {
      events: [TEAM_SETUP_AREAS_UPDATED]
    }
  );

  if (data) {
    return (
      <AllTeamSetupAreasReactContext.Provider
        value={{
          areas: data
        }}
      >
        <PopupContext>
          {props.children}
        </PopupContext>
      </AllTeamSetupAreasReactContext.Provider>
    );
  }
}

export function useAllTeamSetupAreasContextRequired(): AllTeamSetupAreasContextType {
  const context = React.useContext(AllTeamSetupAreasReactContext);
  if (!context) {
    throw new Error('useAllTeamSetupAreasContext must be used within a AllTeamSetupAreasContext');
  }
  return context;
}
